import {
  Checkbox,
  DATE_TO_STRING,
  DateOfBirthInput,
  DatePicker,
} from "components/inputs";
import type { MaskType } from "components/inputs/TextInput/MaskedInput";
import { format } from "date-fns";
import type { ComponentType } from "react";
import type {
  Control,
  FieldErrors,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";
import { Controller } from "react-hook-form";
import { useDebouncedCallback } from "use-debounce";
import { DATE_FORMAT } from "utils/date-utils";
import { useSelector } from "utils/redux";

interface InputComponentProps {
  value: string | number | readonly string[] | undefined;
  onChange:
    | ((event: { target: { value: string; name: string } }) => void)
    | undefined;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  name: string;
  mask: MaskType;
  label?: string;
  errors?: FieldErrors;
  readOnly?: boolean;
  maxLength?: number;
  classNames?: string;
  isFocused?: boolean;
  type?: string;
  placeholder?: string;
}

type ControlledInputProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  label?: string;
  mask?: MaskType;
  errors: FieldErrors<T>;
  onSave?: (value: string | number) => Promise<void>;
  componentType: ComponentType<InputComponentProps>;
  readOnly?: boolean;
  maxLength?: number;
  classNames?: string;
  type?: string;
  placeholder?: string;
  rules?: RegisterOptions<T, Path<T>>;
};

type ControlledDateInputProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  label?: string | null;
  errors: FieldErrors<T>;
  onSave: (value: string | number) => Promise<void>;
  readOnly?: boolean;
  allowFutureDates?: boolean;
  classNames?: string;
  rules?: RegisterOptions<T, Path<T>>;
  hidden?: boolean;
  isClearable?: boolean;
};

type ControlledCheckboxProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  label: string;
  errors?: FieldErrors<T>;
  onSave?: (value: boolean) => Promise<void>;
  readOnly?: boolean;
  disabledReason?: string;
};

export const ControlledInput = <T extends FieldValues>({
  control,
  name,
  label,
  errors,
  onSave,
  componentType: InputComponent,
  readOnly = false,
  mask = "none",
  maxLength,
  classNames,
  type,
  placeholder,
  rules,
}: ControlledInputProps<T>) => {
  const focusedField = useSelector((state) => state.main.focusedField);
  const debouncedSave = useDebouncedCallback((value: string | number) => {
    onSave?.(value);
  }, 500);

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <InputComponent
          {...field}
          isFocused={focusedField === name}
          classNames={classNames}
          value={field.value || ""}
          mask={mask}
          type={type}
          placeholder={placeholder}
          onChange={(e: { target: { value: string; name: string } }) => {
            field.onChange(e.target.value);
            if (onSave && !readOnly) {
              debouncedSave(e.target.value);
            }
          }}
          onBlur={field.onBlur}
          label={label}
          errors={errors}
          readOnly={readOnly}
          maxLength={maxLength}
        />
      )}
    />
  );
};

export const ControlledDateInput = <T extends FieldValues>({
  control,
  name,
  label,
  allowFutureDates = false,
  errors,
  onSave,
  classNames,
  rules,
  hidden = false,
  readOnly = false,
  isClearable = true,
}: ControlledDateInputProps<T>) => {
  const focusedField = useSelector((state) => state.main.focusedField);
  const debouncedSave = useDebouncedCallback((value: string | number) => {
    onSave(value);
  }, 500);

  if (hidden) {
    return null;
  }

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => {
        return (
          <DatePicker
            className={classNames}
            key={field.value}
            {...field}
            allowFutureDates={allowFutureDates}
            label={label || ""}
            readOnly={readOnly}
            isFocused={focusedField === name}
            isClearable={isClearable}
            initialDate={field.value ? new Date(field.value) : undefined}
            onDateChange={(date: Date | null) => {
              field.onChange(date ? DATE_TO_STRING(date) : null);
              if (!readOnly) {
                debouncedSave(date ? format(date, DATE_FORMAT) : null);
              }
            }}
            errors={errors}
          />
        );
      }}
    />
  );
};

export const ControlledCheckbox = <T extends FieldValues>({
  control,
  name,
  label,
  onSave,
  readOnly,
  disabledReason,
  errors = {},
}: ControlledCheckboxProps<T>) => {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Checkbox
          {...field}
          label={label}
          checked={!!field.value}
          readOnly={readOnly}
          disabledReason={disabledReason}
          errors={errors}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (!readOnly) {
              field.onChange(e.target.checked);
              onSave?.(e.target.checked);
            }
          }}
        />
      )}
    />
  );
};

export const ControlledDateOfBirthInput = <T extends FieldValues>({
  control,
  name,
  label = "Date of Birth",
  allowFutureDates = false,
  errors,
  onSave,
  classNames,
  rules,
  hidden = false,
  readOnly = false,
}: ControlledDateInputProps<T>) => {
  const focusedField = useSelector((state) => state.main.focusedField);
  const debouncedSave = useDebouncedCallback((value: string | number) => {
    onSave(value);
  }, 500);

  if (hidden) {
    return null;
  }

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => {
        return (
          <DateOfBirthInput
            className={classNames}
            key={field.value}
            {...field}
            allowFutureDates={allowFutureDates}
            label={label || "Date of Birth"}
            readOnly={readOnly}
            isFocused={focusedField === name}
            initialDate={field.value ? new Date(field.value) : null}
            onDateChange={(date: Date | null) => {
              field.onChange(date ? DATE_TO_STRING(date) : null);
              if (!readOnly) {
                debouncedSave(date ? format(date, DATE_FORMAT) : null);
              }
            }}
            errors={errors}
          />
        );
      }}
    />
  );
};
